import { useAuthContext } from '@/lib/auth-context'
import { useBearer, Section, Pager, usePagedQuery } from './_admin-utils'

interface Notification {
  id: string
  title: string
  created_at: string
  type: string
  message?: string
  body?: string
  read_at?: string
}

interface FetcherParams {
  page: number
  pageSize: number
}

export default function Notifications() {
  const { profile } = useAuthContext()
  const bearer = useBearer()
  const isAdmin = ['admin', 'superadmin'].includes(profile?.role || '')

  const fetcher = async ({ page, pageSize }: FetcherParams) => {
    if (!bearer) return { items: [], total: 0, page, pageSize }
    const origin = typeof window !== 'undefined' ? window.location.origin : ''
    const u = new URL('/api/v1/admin/notifications', origin || 'http://localhost')
    u.searchParams.set('limit', String(pageSize))
    const res = await fetch(u, { headers: { ...bearer } })
    const json = await res.json().catch(() => ({}))
    if (!res.ok) throw new Error(json?.error?.message || json?.error || 'Query failed')
    const items = Array.isArray(json?.data) ? json.data : Array.isArray(json?.items) ? json.items : []
    return { items, total: items.length }
  }

  const {
    items,
    page,
    pageSize,
    setPage,
    setPageSize,
    pageCount,
    loading,
    error,
    load,
  } = usePagedQuery(fetcher, [bearer])

  const markRead = async (ids: string[], markAll = false) => {
    if ((!markAll && !ids?.length) || !bearer) return
    if (markAll) {
      const res = await fetch('/api/v1/admin/notifications/read-all', {
        method: 'POST',
        headers: { 'content-type': 'application/json', ...bearer },
      })
      if (res.ok) await load(page)
    } else {
      await Promise.all(
        ids.map((id) =>
          fetch(`/api/v1/admin/notifications/${id}/read`, {
            method: 'POST',
            headers: { 'content-type': 'application/json', ...bearer },
          }),
        ),
      )
      await load(page)
    }
  }

  if (!isAdmin) {
    return <div className="p-6 text-sm text-cocoa/70">You do not have permission to view this page.</div>
  }

  const unreadIds = ((items || []) as Notification[]).filter((n) => !n.read_at).map((n) => n.id)

  return (
    <div className="p-4 min-w-0">
      <Section
        title="Admin Notifications"
        actions={
          <div className="flex items-center gap-3 text-sm">
            <button
              type="button"
              onClick={() => markRead(unreadIds, true)}
              disabled={!unreadIds.length}
              className="rounded-full border border-white/70 px-3 py-1 text-xs font-semibold uppercase tracking-wide text-cocoa hover:bg-shell disabled:opacity-50"
            >
              Mark all read
            </button>
            <a href="/portal/admin" className="text-sm underline">
              ← Admin
            </a>
          </div>
        }
      >
        {loading && <div className="text-sm">Loading…</div>}
        {error && <div className="text-sm text-red-600">Error: {String(error)}</div>}
        <ul className="divide-y rounded border bg-white">
          {((items || []) as Notification[]).map((n) => (
            <li key={n.id} className="flex items-start justify-between gap-3 p-3">
              <div>
                <div className="font-medium text-cocoa">{n.title}</div>
                <div className="text-xs text-cocoa/70">
                  {new Date(n.created_at).toLocaleString()} • {n.type}
                </div>
                <div className="mt-1 text-sm text-cocoa/80">{n.message || n.body || 'Notification'}</div>
              </div>
              <div className="shrink-0">
                {!n.read_at ? (
                  <button
                    type="button"
                    className="rounded border px-2 py-1 text-sm text-cocoa shadow-sm hover:bg-shell/40"
                    onClick={() => markRead([n.id])}
                  >
                    Mark read
                  </button>
                ) : (
                  <span className="text-xs uppercase tracking-wide text-emerald-600">Seen</span>
                )}
              </div>
            </li>
          ))}
          {!loading && (!items || items.length === 0) ? (
            <li className="p-4 text-cocoa/60">No notifications.</li>
          ) : null}
        </ul>

        <div className="mt-3 flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
          <Pager page={page} pageCount={pageCount} onPage={setPage} />
          <div className="text-sm text-cocoa">
            <span className="mr-2">Page size:</span>
            <select
              className="rounded border px-2 py-1"
              value={pageSize}
              onChange={(event) => setPageSize(Number.parseInt(event.target.value, 10))}
            >
              {[10, 20, 50, 100].map((n) => (
                <option key={n} value={n}>
                  {n}
                </option>
              ))}
            </select>
          </div>
        </div>
      </Section>
    </div>
  )
}
